import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  IPatientClinicianForm,
  IPromsTableData,
} from "../../../../@types/Proms";
import { ReactComponent as PromsEmptyState } from "../../../../assets/images/proms-empty-state.svg";
import { InfiniteTable } from "../../../../components/InfiniteTable";
import { TableSkeleton } from "../../../../components/TableSkeleton";
import { TableSorted } from "../../../../components/TableSorted";
import { useModal } from "../../../../hooks/useModal";
import { promsService } from "../../../../services/promsService";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { AddFormModal } from "../AddFormModal";
import { formsTableColumns } from "../FormsSection/formsTableColumns";
import { promsTableColumns } from "../PromsSection/promsTableColumns";
import { PromDetailsModal } from "./components/PromDetailsModal";
import {
  AddFormTitle,
  ImageContainer,
  NoContentMessage,
  PromsTableContainer,
  PromsTableInnerContainer,
} from "./PatientPromsTable.style";

type PatientNotesTableProps = {
  patientId: number;
  content: IPromsTableData[];
  isLoading?: boolean;
  isError?: boolean;
  isForms?: boolean;
  refreshTable: () => void;
  // Infinite scroll props (optional for backward compatibility)
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  useInfiniteScroll?: boolean;
};

export function PatientPromsTable({
  patientId,
  isLoading,
  isError,
  content,
  isForms,
  refreshTable,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  useInfiniteScroll: enableInfiniteScroll = false,
}: PatientNotesTableProps) {
  const { t } = useTranslation();

  const { openModal, closeModal } = useModal();

  const [clinicianForms, setClinicanForms] = useState<IPatientClinicianForm[]>(
    []
  );

  useEffect(() => {
    // Only fetch clinician form templates when displaying the Forms section
    if (!isForms) return;

    (async () => {
      const { data } = await promsService.getPatientClinicanForms(
        patientId.toString()
      );

      setClinicanForms(data?.result ?? []);
    })();
  }, [isForms, patientId]);

  if (isLoading) {
    return (
      <PromsTableContainer style={{ overflow: "hidden" }}>
        <PromsTableInnerContainer style={{ overflow: "hidden" }}>
          <TableSkeleton columnsNumber={4} rowsNumber={8} />
        </PromsTableInnerContainer>
      </PromsTableContainer>
    );
  }

  if (isError) {
    return (
      <ImageContainer>
        <p>
          {t("patientPage_proms_errorPrefix", {
            kind: isForms
              ? t("patientPage_proms_errorForms")
              : t("patientPage_proms_errorProms"),
          })}{" "}
          <button type="button" onClick={refreshTable}>
            {t("patientPage_proms_tryAgain")}
          </button>
          {t("patientPage_proms_errorSuffix")}
        </p>
      </ImageContainer>
    );
  }

  const handleRowClick = async (rowData: IPromsTableData) => {
    openModal(<PromDetailsModal patientId={patientId} formData={rowData} />, {
      width: "auto",
      showCloseButton: true,
    });
  };

  const handleAddForm = () => {
    if (!patientId) return;

    mixpanelActions.track("User Action: Add Form (table)");

    openModal(
      <AddFormModal
        patientId={patientId.toString()}
        clinicianForms={clinicianForms}
        onClose={() => closeModal()}
      />,
      {
        width: "100%",
        height: "100%",
        showCloseButton: true,
      }
    );
  };

  const handleEmptyStateMessage = () => {
    if (isForms) {
      if (clinicianForms.length === 0) {
        return t("patientPage_proms_emptyFormsNoAccess");
      }

      return t("patientPage_proms_emptyForms");
    }

    return t("patientPage_proms_emptyPromsSetup");
  };

  const tableData: IPromsTableData[] = (content ?? []).map((row) => {
    const times = row.timesCompleted ?? 0;
    const archived = row.numberOfArchivedAnswers ?? 0;

    const timesCompletedDisplay =
      archived > 0
        ? t("patientPage_proms_timesWithArchived", { times, archived })
        : `${times}`;

    return {
      ...row,
      timesCompletedDisplay,
    };
  });

  if (content && content.length > 0) {
    // Use InfiniteTable when infinite scroll is enabled
    if (enableInfiniteScroll && fetchNextPage) {
      return (
        <>
          {isForms && clinicianForms.length > 0 && (
            <AddFormTitle onClick={handleAddForm}>
              {t("patientPage_proms_addForm")}
            </AddFormTitle>
          )}
          <PromsTableContainer>
            <PromsTableInnerContainer>
              <InfiniteTable
                data={tableData}
                columns={isForms ? formsTableColumns : promsTableColumns}
                onRowClick={handleRowClick}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                skeletonColumns={4}
                skeletonRows={8}
              />
            </PromsTableInnerContainer>
          </PromsTableContainer>
        </>
      );
    }

    // Default to regular TableSorted for backward compatibility
    return (
      <>
        {isForms && clinicianForms.length > 0 && (
          <AddFormTitle onClick={handleAddForm}>
            {t("patientPage_proms_addForm")}
          </AddFormTitle>
        )}
        <PromsTableContainer>
          <PromsTableInnerContainer>
            <TableSorted
              data={tableData}
              columns={isForms ? formsTableColumns : promsTableColumns}
              onRowClick={handleRowClick}
            />
          </PromsTableInnerContainer>
        </PromsTableContainer>
      </>
    );
  }

  return (
    <>
      {isForms && clinicianForms.length > 0 && (
        <AddFormTitle onClick={handleAddForm}>
          {t("patientPage_proms_addForm")}
        </AddFormTitle>
      )}
      <ImageContainer>
        <PromsEmptyState width={181} height={158} />
        <NoContentMessage>{handleEmptyStateMessage()}</NoContentMessage>
      </ImageContainer>
    </>
  );
}
